import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Button,
  IconButton,
  Modal
} from '@mui/material';
import { Link, useHistory } from "react-router-dom";
import { StyledTableRowBinary } from 'src/shared/Styles/Styles';
import { ROUTE_PATHS } from 'src/routes';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { visuallyHidden } from '@mui/utils';
import CloseIcon from '@mui/icons-material/Close';
import { modalStyle } from 'src/shared/Styles/Styles';
import { AddLiveCatalog } from '../modals/AddLiveCatalog';
import { useStoreContext } from 'src/store/context';
import { useAddAlert } from 'src/store/actions';
import { getLiveCatalogCall } from 'src/apis/getCatalogCalls';
import { alertMessages } from 'src/constants';
import LoadingSpinner from 'src/shared/loading/loading';

interface Data {
  gsi_parameter_catalog_id: string
  catalog_name: string
  herb_temp_min_min: number,
  herb_temp_min_max: number,
  woody_temp_min_min: number,
  woody_temp_min_max: number,

}

/***
 * Following is used as an Enhanced header to filter the headers as needed
 * 
 */
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'catalog_name',
    numeric: false,
    disablePadding: false,
    label: 'CATALOG NAME',
  },
  {
    id: 'herb_temp_min_min',
    numeric: true,
    disablePadding: false,
    label: 'HERBACEOUS TEMP MIN MIN',
  },
  {
    id: 'herb_temp_min_max',
    numeric: true,
    disablePadding: false,
    label: 'HERBACEOUS TEMP MIN MAX',
  },
  {
    id: 'woody_temp_min_min',
    numeric: true,
    disablePadding: false,
    label: 'WOODY TEMP MIN MIN',
  },
    {
    id: 'woody_temp_min_max',
    numeric: true,
    disablePadding: false,
    label: 'WOODY TEMP MIN MAX',
  }
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}
/***
 * Used for styling the Cells for the inner tables
 * 
 */
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort}=
    props;

    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          (
            <TableCell
              className='headerParent'
              style={{backgroundColor: '#d9d9d9', width: '20%'}}
              key={headCell.id}
              tabIndex={0}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : <Box component="span" sx={visuallyHidden}></Box>}

                </TableSortLabel>

              }
            </TableCell>
          )
        ))}
      </TableRow>
    </TableHead>
  );
}

function LiveFuelCatalogTable() {

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('catalog_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openLiveModal, setOpenLiveModal] = useState<boolean>(false);
  const [createUpdateLiveSuccess, setCreateUpdateLiveSuccess] = useState<boolean>(false);
  const [, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);

  const [searchOptions, setSearchOptions] = useState<any[]>([])
  const [liveCatalogTable, setDroughtCatalogTable] = useState<any[]>([]);
  const history = useHistory();
  const [loading,setLoading] = useState<boolean>(false)

  /** Make the api call for the drought catalog table */
    const liveCatalogCallback = useCallback((request: any, search?:boolean) => {
      (async () => {
        try {
          setLoading(true);
          const liveCatalogTable: any = await getLiveCatalogCall(request, addAlert);
          if (liveCatalogTable.data.length > 0) {
            if(search){
               setSearchOptions(liveCatalogTable.data)
            }
            else{
              setDroughtCatalogTable(liveCatalogTable.data);
              setTotalCount(liveCatalogTable._metadata.total_count); 
            }
          } else {
            if(search) {
              setSearchOptions([]);
            }
            else {
              setDroughtCatalogTable([]);
              setTotalCount(0);
            }
            setCreateUpdateLiveSuccess(false);
          }
          setLoading(false);
  
        } catch (error) {
          setLoading(false);
          if(search){
            setSearchOptions([])
          }
          else{
            setDroughtCatalogTable([]);
            setTotalCount(0);
          }
          addAlert({
            id: '',
            message: alertMessages.retrieveCatalog,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          });
        }
      })();
    }, [addAlert]);

  
    /*call for live catalog table*/
    useEffect(() => {
      const request = {
        id: null,
        perPage: rowsPerPage,
        page: page,
        sortBy: orderBy,
        sortOrder: order
      }
      liveCatalogCallback(request);
    }, [liveCatalogCallback, order, orderBy, page, rowsPerPage]);

    /*successful save or update*/
    useEffect(() => {
      if(createUpdateLiveSuccess){
        const request = {
          id: null,
          catalogName: null,
          perPage: rowsPerPage,
          page: page,
          sortBy: orderBy,
          sortOrder: order
        }
        liveCatalogCallback(request);
        //call for search options
        const requestSearch = {
          sortBy: 'catalog_name',
          sortOrder: 'asc'
        }
        liveCatalogCallback(requestSearch, true);
      }
    }, [liveCatalogCallback, order, orderBy, page, rowsPerPage, createUpdateLiveSuccess]);

    useEffect(()=>{
      //call for search options
      const requestSearch = {
        id: null,
        perPage: 10000,
        page: 0,
        sortBy: 'catalog_name',
        sortOrder: 'asc'
      }
      liveCatalogCallback(requestSearch, true);
    },[liveCatalogCallback])

  const onDroughtCatalogSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
    history.push(
      { pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.liveTab}/detail/${incomingVal?.gsiParameterCatalogId}`, state: {...incomingVal, from: 'search'} }
    )
    
  }

  const handleRequestSort = (event:React.MouseEvent<unknown>, property:keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0)
    const requestSortUpdate={
      perPage: rowsPerPage,
      page: 0,
      sortBy: property,
      sortOrder: isAsc ? 'desc' : 'asc'
    }
    liveCatalogCallback(requestSortUpdate);
  };


  /** Populate the drop downs for filtering the station table */

  const getLinkToCatalog = (catalog:any) => {
    return { pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.liveTab}/detail/${catalog?.gsiParameterCatalogId}`, state: {...catalog} }
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const updatePageCall = {
      perPage: rowsPerPage,
      page: newPage,
      sortBy: orderBy,
      sortOrder: order
    };
    liveCatalogCallback(updatePageCall);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const updatePerPage = {
      perPage: Number.parseInt(event.target.value, 10),
      page: 0,
      sortBy: orderBy,
      sortOrder: order
    };
    liveCatalogCallback(updatePerPage);
  };

  const handleCloseLiveModal = (_event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      setOpenLiveModal(false);
    }
    setOpenLiveModal(false);
  }

  return (
    <div style={{overflowX: 'hidden', marginLeft: '.5rem', width: '95%'}}>
      {(loading) && (

        <div style={{ height: '30%' }}>
          <LoadingSpinner />
        </div>
      )}
      <Modal //Modal to open edit/create live fuel
        className="smooth-scrollbar"
        open={openLiveModal}
        onClose={handleCloseLiveModal}
      >
        <div>
          <IconButton aria-label='close' style={{ left: '73.5%', top: '5.5%', position: 'absolute', cursor: 'pointer' }} onClick={() => { setOpenLiveModal(false) }}>
            <CloseIcon style={{ color: 'white' }} />
          </IconButton>
          <Box sx={modalStyle} className="smooth-scrollbar removePadding" >
            {
              <AddLiveCatalog setOpen={setOpenLiveModal} setCreateUpdateLiveSuccess={setCreateUpdateLiveSuccess}/>
            }
          </Box>
        </div>
      </Modal>
      {/* Live Fuel Filters Section */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <div
          style={{
            marginLeft: '1.5%',
            paddingTop: '2%',
            maxWidth: '90vw',
          }}
        >
          <div>
            <div className="font-sans-md text-bold" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {/* Search Drought Catalog */}
              <div style={{ width: '37%'}}>
                <label
                  htmlFor="station"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Search Live Fuel Catalogs
                </label>
                <div
                  className="searchStationsDownloadPage"
                >
                  {/* Search Icons */}
                  <i
                    className="fas fa-search"
                  />
                  <Autocomplete
                    id="liveFuelCatalogSearch"
                    isOptionEqualToValue={(option,value)=>option?.gsiParameterCatalogId===value?.gsiParameterCatalogId}
                    options={searchOptions}
                    getOptionLabel={(option)=>`${option?.catalogName}`}
                    onChange={(event, newValue) => onDroughtCatalogSearch(event, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        aria-label={'Search Live Fuel Catalog...'}
                        placeholder="Search Live Fuel Catalog..."
                      />
                    )}
                  />
                </div>
              </div>
              <div style={{ width: '20%', marginRight: 0, marginLeft: 'auto'}}>
              <Button
                variant='contained'
                onClick={()=> {setOpenLiveModal(true)}}
              >
                Create New Catalog</Button>
              </div>
            </div>
          </div>

        </div>

      {/* Table Section */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', paddingTop: '1rem' }}>

        <TableContainer
          component={Paper}
          className='margin-top-1'
          style={{
            width: '100%',
            maxWidth:'100%',
          }}
        >
          <Table stickyHeader>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {liveCatalogTable?.map((row, index) => (
                <StyledTableRowBinary
                  key={`${row?.catalogName}-${index}`}
                  hover
                  style={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    borderRadius: '4px',
                  }}
                >
                  <TableCell className='text-align-override'>
                    <Link
                      to={getLinkToCatalog(row)}
                      color="primary"
                      style={{ fontWeight: 500 }}
                    >
                      {row?.catalogName}
                    </Link>
                  </TableCell>
                  <TableCell className='text-align-override'>
                    {row?.herbTempMinMin}
                  </TableCell>
                  <TableCell className='text-align-override'>
                    {row?.herbTempMinMax}
                  </TableCell>
                  <TableCell className='text-align-override'>
                    {row?.woodyTempMinMin}
                  </TableCell>
                  <TableCell className='text-align-override'>
                    {row?.woodyTempMinMin}
                  </TableCell>
                </StyledTableRowBinary>
              ))}
            </TableBody>
          </Table>
          <CustomTablePagination
            rowsPerPageOptions={[10, 20, 30]}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />   
        </TableContainer>
      </Box>
      </Box>
    </div>
  );
}

export default LiveFuelCatalogTable;